import { ApiError, createLocation, getOrgBranding } from '@jq4race/api-client';
import { LocalizedError, useTranslation } from '@jq4race/i18n';
import type { CreateLocationRequest, OrgNiche } from '@jq4race/shared';
import { buttonVariants, Card, CardContent } from '@jq4race/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { PageHeader } from '../../../app-shell/ui/PageHeader';
import { LocationForm } from '../components/LocationForm';

export function NewLocationPage() {
  const { t } = useTranslation('locations');
  const navigate = useNavigate();
  // QA-oct: default the niche to the organization's (a hotel org was getting
  // "Restaurant"). The form mounts only after the lookup settles because
  // react-hook-form reads defaultValues once; on failure it falls back to the
  // form's own default so creating a location is never blocked.
  const [orgNiche, setOrgNiche] = useState<OrgNiche | null>(null);
  const [nicheResolved, setNicheResolved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getOrgBranding()
      .then((org) => {
        if (!cancelled) setOrgNiche(org.niche ?? null);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setNicheResolved(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSubmit(values: CreateLocationRequest) {
    try {
      await createLocation(values);
      void navigate({ to: '/dashboard/locations' });
    } catch (e) {
      if (e instanceof ApiError) {
        throw new LocalizedError(translateApiError(e, t, { fallback: t('page.new.createError') }));
      }
      throw e;
    }
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('page.eyebrow')}
        title={t('page.new.title')}
        description={t('page.new.description')}
        actions={
          <Link
            to="/dashboard/locations"
            className={buttonVariants({ size: 'sm', variant: 'ghost' })}
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
            {t('page.new.backToList')}
          </Link>
        }
      />

      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          {nicheResolved ? (
            <LocationForm
              mode="create"
              defaultValues={orgNiche ? { niche: orgNiche } : undefined}
              onSubmit={handleSubmit}
              submitLabel={t('form.createLocation')}
              onCancel={() => void navigate({ to: '/dashboard/locations' })}
            />
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
